import {
    addChatSmartOfferGenericMessage,
    addChatSmartOfferMessage,
    addChatSmartOfferResponseMessage,
} from "@asurion-core/expert-workspace-timeline";
import { type EligibleProduct, useProductOfferStore } from "@expert/sales-tools";
import { MessageSubType } from "@expert/shared-types";
import { useEffect, useRef } from "react";

import { EligibilityStatus, SaleProgress } from "@/sales-tools/home-product/types";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useSessionId } from "@/sdk/sessions/hooks";

import { useSmartOfferFlowStore } from "./useSmartOfferFlowStore";

export function useSmartOfferMessages(
    selectedProduct: EligibleProduct | undefined,
    isChatSmartOfferFlowEnabled: boolean,
) {
    const logger = useLogger({
        module: "useSmartOfferMessages",
        supportTeam: "messaging",
    });
    const sessionId = useSessionId();
    const sentMessagesRef = useRef<Map<string, Set<string>>>(new Map());
    const sentDeclinedMessageRef = useRef<Set<string>>(new Set());

    const currentStep = useSmartOfferFlowStore((state) => state.getCurrentStep(sessionId));
    const updateStep = useSmartOfferFlowStore((state) => state.updateStep);
    const isSmartOfferActionUsed = useSmartOfferFlowStore((state) => state.isSmartOfferActionUsed);

    const productInfo = selectedProduct
        ? useProductOfferStore.getState().getProductInfo(sessionId, selectedProduct.productSku)
        : undefined;

    useEffect(() => {
        if (!isChatSmartOfferFlowEnabled) return;

        if (!selectedProduct || !productInfo) {
            return;
        }

        const { productSku, saleProgress, status } = selectedProduct;
        const storedState = useSmartOfferFlowStore.getState().steps[sessionId];
        const isNewProduct = storedState?.sku !== productSku;

        if (isNewProduct) {
            sentMessagesRef.current.delete(sessionId);
        }

        // If product has changed, reset to initial step
        if (isNewProduct && saleProgress === SaleProgress.InProgress && status === EligibilityStatus.Eligible) {
            updateStep(sessionId, productSku, "SuggestedOfferStep");
            return;
        }
    }, [isChatSmartOfferFlowEnabled, selectedProduct, sessionId, productInfo, updateStep]);

    // Send message when step changes
    useEffect(() => {
        if (!currentStep || !productInfo || !selectedProduct) return;

        const { productSku } = selectedProduct;
        const messageKey = `${productSku}-${currentStep}`;

        // Get or create session set
        if (!sentMessagesRef.current.has(sessionId)) {
            sentMessagesRef.current.set(sessionId, new Set());
        }
        const sessionMessages = sentMessagesRef.current.get(sessionId)!;

        // Skip if already sent in THIS browser session
        if (sessionMessages.has(messageKey)) {
            return;
        }

        let message = "";
        let tag = "";
        let subType: MessageSubType = "offer";

        switch (currentStep) {
            case "SuggestedOfferStep":
                addChatSmartOfferResponseMessage(sessionId, `Offer ${productInfo.displayName}`);
                message = productInfo.chatSmartOfferMessage ?? "";
                tag = "Offer message";
                subType = "offer";
                break;
            case "SuggestedEnrollmentStep":
                message = productInfo.chatEnrollmentMessage ?? "";
                tag = "Enrollment message";
                subType = "enrollment";
                break;
            case "WaitingOnEnrollmentStep":
                message = productInfo.chatWaitingOnEnrollmentMessage ?? "";
                subType = "waitingEnrollment";
                break;
            case "CelebratoryMessageStep":
                addChatSmartOfferResponseMessage(sessionId, "Customer enrollment successful");
                addChatSmartOfferGenericMessage(
                    sessionId,
                    "Nice sale! Congratulate the customer and ask if they have any follow up questions about their new plan.",
                );
                message = productInfo.saleSuccessMessage ?? "";
                tag = "Celebratory message";
                subType = "celebratory";
                break;
        }

        if (message) {
            addChatSmartOfferMessage(sessionId, message, subType, tag);
            sessionMessages.add(messageKey);
            logger.info({ step: currentStep, sessionId, subType }, "Smart offer message sent");
        }
    }, [currentStep, logger, sessionId, productInfo, selectedProduct]);

    // Watch for successful enrollment and move to celebration step
    useEffect(() => {
        if (!isChatSmartOfferFlowEnabled || !selectedProduct) return;

        const { saleProgress, productSku } = selectedProduct;

        // If sale is successful and we're not already on celebration step, move to it
        if (saleProgress === SaleProgress.Success && currentStep !== "CelebratoryMessageStep") {
            updateStep(sessionId, productSku, "CelebratoryMessageStep");
            logger.info({ sessionId, productSku }, "Moving to celebration step after successful enrollment");
        }
    }, [
        selectedProduct?.saleProgress,
        currentStep,
        isChatSmartOfferFlowEnabled,
        logger,
        selectedProduct,
        sessionId,
        updateStep,
    ]);

    // Watch for declined state and send appropriate response message
    useEffect(() => {
        if (!isChatSmartOfferFlowEnabled || !selectedProduct) return;

        const { saleProgress } = selectedProduct;

        if (saleProgress === SaleProgress.Declined) {
            // Prevent sending the message multiple times
            if (sentDeclinedMessageRef.current.has(sessionId)) {
                return;
            }

            // Simple check: if "decline" action was used, expert clicked it
            const expertDeclined = isSmartOfferActionUsed(sessionId, "decline");

            if (expertDeclined) {
                addChatSmartOfferResponseMessage(sessionId, "Declined");
                logger.info({ sessionId, declinedBy: "expert" }, "Expert declined enrollment");
            } else {
                addChatSmartOfferResponseMessage(sessionId, "Customer declined");
                logger.info({ sessionId, declinedBy: "customer" }, "Customer declined enrollment");
            }

            sentDeclinedMessageRef.current.add(sessionId);
        }
    }, [
        selectedProduct?.saleProgress,
        isChatSmartOfferFlowEnabled,
        selectedProduct,
        sessionId,
        isSmartOfferActionUsed,
        logger,
    ]);
}
